import { Paper, Typography, Box, useTheme } from "@mui/material";
import { ZincProduct } from "@t3/shared";
import Link from "@/components/link";
import ProductImage from "@/components/productImage";
import convertToPoints from "@/utils/convertToPoints";
import { useSearchParams } from "react-router-dom";

export const ProductCard = ({ product }: { product: ZincProduct }) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  return (
    <Link
      to={`/shop/products/${product.product_id}?${searchParams.toString()}`}
      style={{ textDecoration: "none" }}
    >
      <Paper
        data-cypress={`product-${product.product_id}`}
        elevation={2}
        sx={{
          width: "100%",
          height: { xs: "auto", sm: "auto", md: "auto", lg: "auto" },
          minHeight: { xs: "auto", sm: 240, md: 340, lg: 390 },
          margin: 0,
          padding: { xs: "22px 12px", sm: "16px", md: "16px" },
          textAlign: { xs: "left", sm: "left", md: "center" },
          display: "flex",
          flexDirection: { xs: "row", sm: "column", md: "column" },
          alignItems: {
            xs: "center",
            sm: "center",
            md: "center",
          },
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          transition:
            "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out",
          "&:hover": {
            transform: "scale(1.03)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderColor: "primary.main",
            background: "linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)",
          },
        }}
      >
        <Box
          sx={{
            flex: {
              xs: "0 0 auto",
              sm: "0 0 auto",
              md: "1 0 auto",
            },
            width: {
              xs: "80px",
              sm: "140px",
              md: "200px",
              lg: "260px",
            },
            height: {
              xs: "80px",
              sm: "140px",
              md: "140px",
              lg: "180px",
            },
            marginBottom: { xs: 0, sm: 2, md: 0 },
            marginTop: { xs: 0, sm: 2, md: 4 },
            marginRight: { xs: 2, sm: 0, md: 0 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ProductImage
            card={product}
            margin="auto"
            width={{
              xs: "80px",
              sm: "140px",
              md: "200px",
              lg: "260px",
            }}
            height={{
              xs: "80px",
              sm: "140px",
              md: "140px",
              lg: "180px",
            }}
          />
        </Box>

        <Box
          sx={{
            paddingLeft: { xs: 0, sm: 0, md: 0 },
            display: "flex",
            flexDirection: "column",
            justifyContent: {
              xs: "center",
              sm: "center",
              md: "center",
            },
            overflow: "hidden",
            width: "100%",
            flex: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: 14, sm: 15, md: 16, lg: 18 },
              fontWeight: 600,
              width: "100%",
              marginTop: { xs: 0, sm: 1, md: 1 },
              marginBottom: { xs: 0.5, sm: 1, md: 1 },
              display: "-webkit-box",
              overflow: "hidden",
              textOverflow: "ellipsis",
              WebkitLineClamp: { xs: 3, sm: 3, md: 3 },
              WebkitBoxOrient: "vertical",
              textAlign: { xs: "left", sm: "center", md: "center" },
              color: "text.primary",
              lineHeight: 1.3,
            }}
          >
            {product.title}
          </Typography>

          {/* <StarRating stars={product.stars} numReviews={product.num_reviews} /> */}

          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
              color: "white",
              padding: {
                xs: "4px 8px",
                sm: "6px 10px",
                md: "8px 14px",
              },
              width: { xs: "auto", sm: "auto" },
              borderRadius: "0 0 0 1rem",
              textAlign: "center",
              boxShadow: `0 4px 12px ${theme.palette.secondary.main}4D`,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: `0 6px 16px ${theme.palette.secondary.main}66`,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: 11, sm: 12, md: 13 },
                fontWeight: 700,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
            >
              {convertToPoints(product.amount)} Points
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Link>
  );
};
